import toast from "react-hot-toast";
import { Task } from "../types";
import { showTaskLinkToast } from "../lib/undoStack";

export interface UseSubtaskConversionHandlersArgs {
  editingTask: Task | null;
  tasks: Task[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  navigate: any;
  moveSubtaskInDb: (
    subtaskId: string,
    sourceTaskId: string,
    targetTaskId: string,
  ) => Promise<boolean>;
  convertSubtaskToTaskInDb: (subtaskId: string) => Promise<Task | null>;
  convertTaskToSubtaskInDb: (
    taskId: string,
    targetTaskId: string,
  ) => Promise<boolean>;
}

export interface UseSubtaskConversionHandlersResult {
  handleMoveSubtask: (subtaskId: string, targetTaskId: string) => Promise<void>;
  handleConvertSubtaskToTask: (subtaskId: string) => Promise<void>;
  handleConvertTaskToSubtask: (
    taskId: string,
    targetTaskId: string,
  ) => Promise<void>;
}

/**
 * The three ways a subtask/task moves across the parent-child boundary, all
 * reached from the task dialog's breadcrumb menus: moving a subtask to a
 * different parent, promoting one to its own task, and demoting a task into
 * someone else's subtask. Split out of {@link useTaskEditing} — it's a
 * grandfathered, size-capped file that may shrink but not grow.
 *
 * Each ends by navigating to wherever the moved/converted item now lives,
 * and raises a toast naming it there as a clickable link back to that same
 * place — useful once the toast outlives the auto-navigation, e.g. after the
 * user has since navigated elsewhere.
 */
export function useSubtaskConversionHandlers({
  editingTask,
  tasks,
  navigate,
  moveSubtaskInDb,
  convertSubtaskToTaskInDb,
  convertTaskToSubtaskInDb,
}: UseSubtaskConversionHandlersArgs): UseSubtaskConversionHandlersResult {
  const handleMoveSubtask = async (subtaskId: string, targetTaskId: string) => {
    if (!editingTask) return;
    // Read the name before the move. A target that is not in the local
    // list yet has no title, and `"${undefined}"` would print "undefined".
    const targetTitle = tasks.find((t) => t.id === targetTaskId)?.title;
    const success = await moveSubtaskInDb(
      subtaskId,
      editingTask.id,
      targetTaskId,
    );
    if (!success) return;

    const goToTarget = () =>
      navigate({
        to: "/task/$taskId",
        params: { taskId: targetTaskId },
        search: (prev: Record<string, unknown>) => ({
          ...prev,
          subtask: subtaskId,
        }),
      });

    if (targetTitle) {
      showTaskLinkToast({
        prefix: "Subtask moved to ",
        label: `"${targetTitle}"`,
        onLabelClick: goToTarget,
      });
    } else {
      toast.success("Subtask moved");
    }

    goToTarget();
  };

  const handleConvertSubtaskToTask = async (subtaskId: string) => {
    const newTask = await convertSubtaskToTaskInDb(subtaskId);
    if (!newTask) return;

    const goToNewTask = () =>
      navigate({
        to: "/task/$taskId",
        params: { taskId: newTask.id },
        search: (prev: Record<string, unknown>) => {
          const { subtask: _s, ...rest } = prev;
          return rest;
        },
      });

    showTaskLinkToast({
      prefix: "Converted to task ",
      label: `"${newTask.title}"`,
      onLabelClick: goToNewTask,
    });

    goToNewTask();
  };

  const handleConvertTaskToSubtask = async (
    taskId: string,
    targetTaskId: string,
  ) => {
    // Read the target title from the pre-conversion snapshot so the toast
    // can never observe stale state after the conversion mutates `tasks`.
    const targetTitle = tasks.find((t) => t.id === targetTaskId)?.title;
    const goToTarget = () =>
      navigate({
        to: "/task/$taskId",
        params: { taskId: targetTaskId },
        search: (prev: Record<string, unknown>) => prev,
      });

    // Navigate to the target first. If the source task is the one currently
    // open in the dialog, this avoids a transient frame where the route
    // points at a task that's just been removed from local state, which
    // would otherwise flash the "task not found" screen.
    goToTarget();

    const success = await convertTaskToSubtaskInDb(taskId, targetTaskId);
    if (!success) return;

    if (targetTitle) {
      showTaskLinkToast({
        prefix: "Converted to sub-task of ",
        label: `"${targetTitle}"`,
        onLabelClick: goToTarget,
      });
    } else {
      toast.success("Converted to sub-task");
    }
  };

  return { handleMoveSubtask, handleConvertSubtaskToTask, handleConvertTaskToSubtask };
}
